/**
 * Centralized logging utility for the eSignature frontend.
 * Provides structured, timestamped, human-readable logs.
 *
 * Usage:
 *   import logger from './utils/logger';
 *   logger.info('User logged in', { email: 'john@example.com' });
 *   logger.error('API call failed', { endpoint: '/auth/login', status: 500 });
 *   logger.auth('Login successful', { email: 'john@example.com' });
 *
 * Log format:
 *   [2026-03-10 14:32:05.123] [INFO] [Auth] User logged in | email=john@example.com
 *
 * Environment:
 *   - In production (NODE_ENV=production): only WARN and ERROR are logged
 *   - In development: all levels (DEBUG, INFO, WARN, ERROR) are logged
 */

const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
};

// In production, only show warnings and errors to keep console clean
const MIN_LEVEL =
  process.env.NODE_ENV === 'production' ? LOG_LEVELS.WARN : LOG_LEVELS.DEBUG;

/**
 * Format a timestamp with millisecond precision
 */
function getTimestamp() {
  const now = new Date();
  const pad = (n, len = 2) => String(n).padStart(len, '0');
  return (
    `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ` +
    `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}.${pad(now.getMilliseconds(), 3)}`
  );
}

/**
 * Format extra data as key=value pairs
 */
function formatExtra(extra) {
  if (!extra || typeof extra !== 'object' || Object.keys(extra).length === 0) {
    return '';
  }
  const pairs = Object.entries(extra)
    .map(([k, v]) => {
      // Truncate long values
      const val = typeof v === 'string' && v.length > 100 ? v.slice(0, 100) + '...' : v;
      return `${k}=${val}`;
    })
    .join(' ');
  return ` | ${pairs}`;
}

/**
 * Core log function
 */
function log(level, category, message, extra = {}) {
  if (LOG_LEVELS[level] < MIN_LEVEL) return;

  const timestamp = getTimestamp();
  const extraStr = formatExtra(extra);
  const formattedMessage = `[${timestamp}] [${level}] [${category}] ${message}${extraStr}`;

  switch (level) {
    case 'ERROR':
      console.error(formattedMessage);
      break;
    case 'WARN':
      console.warn(formattedMessage);
      break;
    case 'INFO':
      console.info(formattedMessage);
      break;
    case 'DEBUG':
    default:
      console.log(formattedMessage);
      break;
  }
}

/**
 * Create a category-scoped logger
 */
function createCategoryLogger(category) {
  return {
    debug: (msg, extra) => log('DEBUG', category, msg, extra),
    info: (msg, extra) => log('INFO', category, msg, extra),
    warn: (msg, extra) => log('WARN', category, msg, extra),
    error: (msg, extra) => log('ERROR', category, msg, extra),
  };
}

/**
 * Main logger with category sub-loggers for common domains
 */
const logger = {
  // Generic log methods
  debug: (msg, extra) => log('DEBUG', 'App', msg, extra),
  info: (msg, extra) => log('INFO', 'App', msg, extra),
  warn: (msg, extra) => log('WARN', 'App', msg, extra),
  error: (msg, extra) => log('ERROR', 'App', msg, extra),

  // Category-specific loggers
  auth: createCategoryLogger('Auth'),
  api: createCategoryLogger('API'),
  socket: createCategoryLogger('Socket'),
  support: createCategoryLogger('Support'),
  documents: createCategoryLogger('Documents'),
  signing: createCategoryLogger('Signing'),
  navigation: createCategoryLogger('Navigation'),
  upload: createCategoryLogger('Upload'),
  template: createCategoryLogger('Template'),
  theme: createCategoryLogger('Theme'),
  branding: createCategoryLogger('Branding'),

  // Create a custom category logger on the fly
  create: createCategoryLogger,

  // Performance timing helper
  time: (label) => {
    const start = performance.now();
    return {
      end: (extra = {}) => {
        const duration = Math.round(performance.now() - start);
        log('INFO', 'Perf', `${label} completed in ${duration}ms`, { ...extra, duration_ms: duration });
        return duration;
      },
    };
  },
};

export default logger;
